/**
 * shared/space_ew.ts — CLIENT MIRROR of server/src/lib/space_ew.ts (the server copy is authoritative; keep them
 * byte-identical below the headers — verify:space-flight diffs them). Space campaign slice I, 2026-09-30: ONE
 * electronic-warfare effect with counterplay (report §21.6 / §21.10). Pure.
 */
// ── THE NUMBERS (report §21.10 — balancing hypotheses, followed as the prototype) ────────────────────────────────────
// A fitted utility (the Countermeasures pod's EW suite) spends 10 capacitor to apply SIX seconds of sensor interference
// to ONE legal hostile pilot within 600 m and line of sight. The enforceable effect: the target's SERVER-METERED
// sustained work (the survey's assess / stabilize / extract and the mystery's beam work — slice F) is credited at 2/3
// while affected; completed progress is never taken back. The mount then cycles 12 s. The effect BREAKS when the two
// hulls stand more than 750 m apart for one accepted second, when either changes system / location / hull, or when the
// source is destroyed or gone. Effects never stack: the strongest standing effect keeps its ORIGINAL expiry and a new
// activation needs a new paid request. The target's countermeasure (the sensor burst) costs 15 capacitor, clears the
// current effect and resists a new one for 4 s, on a 20 s cooldown.
export const EW_FLAG = 'sensor_ew_v1';
export const EW_FIT_SLOT = 'counter';
export const EW_CAP_COST = 10;
export const EW_DURATION_MS = 6_000;
export const EW_RANGE_M = 600;
export const EW_BREAK_RANGE_M = 750;
export const EW_BREAK_HOLD_MS = 1_000;
export const EW_COOLDOWN_MS = 12_000;
/** The fraction of the target's metered progress the interference removes (the rate becomes 1 − 1/3 = 2/3). */
export const EW_STRENGTH = 1 / 3;
export const ECCM_CAP_COST = 15;
export const ECCM_RESIST_MS = 4_000;
export const ECCM_COOLDOWN_MS = 20_000;
/** The server's break sweep runs this often — only while an effect is live. */
export const EW_SWEEP_MS = 250;
/** An ended effect row stays this long (the HUD's "broken — out of range" and any beat interval still crediting it). */
export const EW_KEEP_ENDED_MS = 30_000;
/** Request ids a pilot's row remembers (a duplicate of one of these never spends twice). */
export const EW_REQ_RING = 16;
export const EW_REQ_RE = /^[A-Za-z0-9_-]{4,40}$/;

export type EwEndReason =
  | 'expired' | 'range' | 'system' | 'transition' | 'life' | 'source_lost' | 'target_lost' | 'countermeasure'
  | 'replaced' | 'war_ended';
export const EW_END_TEXT: Record<EwEndReason, string> = {
  expired: 'ran its six seconds',
  range: 'broken — the hulls stood more than 750 m apart',
  system: 'broken — a hull left the system',
  transition: 'broken — a hull docked, warped or jumped',
  life: 'broken — a hull was lost or changed',
  source_lost: 'broken — the jammer is gone',
  target_lost: 'broken — the target is gone',
  countermeasure: 'cleared by a sensor burst',
  replaced: 'replaced by a stronger effect',
  war_ended: 'broken — the war between the guilds ended',
};

// ── Legality (the ordered refusals of `ewJam`) ────────────────────────────────────────────────────────────────────
export interface JamFacts {
  flagOn: boolean;
  self: boolean;
  targetKnown: boolean;
  bothInGuilds: boolean;
  liveWar: boolean;
  sameSystem: boolean;
  targetInFlight: boolean;
  fitted: boolean;
  rangeM: number;
  los: boolean;
  nowMs: number;
  readyAtMs: number;       // the source's mount cooldown
  resistUntilMs: number;   // the target's sensor-burst resistance
  standing: { strength: number; expiresMs: number } | null; // the strongest live effect on the target
  strength: number;        // this activation's strength
}
/** '' = legal; otherwise the refusal the pilot reads. Nothing is debited on a refusal. */
export function jamProblem(f: JamFacts): string {
  if (!f.flagOn) return 'Sensor interference is offline on this shard';
  if (f.self) return 'That is your own hull';
  if (!f.targetKnown) return 'No fix on that pilot';
  if (!f.bothInGuilds || !f.liveWar) return 'No war stands between your guilds — interference needs a live wardec';
  if (!f.sameSystem) return 'That pilot is in another sky';
  if (!f.targetInFlight) return 'That pilot is docked or in transit — no lock';
  if (!f.fitted) return 'No jammer fitted — the Countermeasures pod carries the EW suite';
  if (!Number.isFinite(f.rangeM) || f.rangeM > EW_RANGE_M) return `Out of jammer range — close to ${EW_RANGE_M} m`;
  if (!f.los) return 'No line of sight — something solid stands between you';
  if (f.nowMs < f.readyAtMs) return `The jammer is cycling — ${Math.ceil((f.readyAtMs - f.nowMs) / 1000)} s`;
  if (f.nowMs < f.resistUntilMs) return `Their sensors are hardened — ${Math.ceil((f.resistUntilMs - f.nowMs) / 1000)} s`;
  if (stackDecision(f.standing, f.strength, f.nowMs) === 'refuse') {
    return `Their sensors are already jammed (${Math.ceil(((f.standing?.expiresMs ?? f.nowMs) - f.nowMs) / 1000)} s left) — effects never stack`;
  }
  return '';
}

/** THE NO-STACK RULE: a standing effect keeps its original expiry unless the new one is STRICTLY stronger (which then
 *  replaces it and runs its own six seconds). Equal strength never refreshes. */
export function stackDecision(standing: { strength: number; expiresMs: number } | null, strength: number, nowMs: number): 'apply' | 'refuse' {
  if (!standing || standing.expiresMs <= nowMs) return 'apply';
  return strength > standing.strength + 1e-9 ? 'apply' : 'refuse';
}

// ── The break clock (the server's sweep, one step per accepted pair of rows) ──────────────────────────────────────────
/** One step of the separation clock: a hull pair more than 750 m apart for one accepted second breaks the effect at
 *  the exact second's end (`brokeAtMs`); any accepted reading within 750 m resets the clock. */
export function breakStep(farSinceMs: number, sepM: number, nowMs: number): { farSinceMs: number; brokeAtMs: number | null } {
  if (!(sepM > EW_BREAK_RANGE_M)) return { farSinceMs: 0, brokeAtMs: null };
  if (farSinceMs <= 0) return { farSinceMs: nowMs, brokeAtMs: null };
  if (nowMs - farSinceMs >= EW_BREAK_HOLD_MS) return { farSinceMs, brokeAtMs: farSinceMs + EW_BREAK_HOLD_MS };
  return { farSinceMs, brokeAtMs: null };
}

// ── The metered rate ───────────────────────────────────────────────────────────────────────────────────────────────
export interface EwWindow { fromMs: number; untilMs: number; strength: number }
/** The credit a metered beat earns when it ends at `nowMs` and covers `creditMs` (slice F's `workCredit`): the part of
 *  the interval inside an effect's window is credited at (1 − strength); overlapping windows count their STRONGEST
 *  (never multiplied). Integer milliseconds, rounded. No window → the credit unchanged. */
export function ewCredit(creditMs: number, nowMs: number, windows: readonly EwWindow[]): number {
  if (!(creditMs > 0)) return Math.max(0, creditMs || 0);
  const a = nowMs - creditMs, b = nowMs;
  const cuts = new Set<number>([a, b]);
  for (const w of windows) {
    if (w.untilMs <= a || w.fromMs >= b) continue;
    cuts.add(Math.max(a, w.fromMs)); cuts.add(Math.min(b, w.untilMs));
  }
  const pts = [...cuts].sort((x, y) => x - y);
  let out = 0;
  for (let i = 0; i + 1 < pts.length; i++) {
    const lo = pts[i], hi = pts[i + 1], mid = (lo + hi) / 2;
    let s = 0;
    for (const w of windows) if (w.fromMs <= mid && mid < w.untilMs) s = Math.max(s, Math.min(1, Math.max(0, w.strength)));
    out += (hi - lo) * (1 - s);
  }
  return Math.round(out);
}

// ── Line of sight ──────────────────────────────────────────────────────────────────────────────────────────────────
/** A solid between two hulls: a sphere (halfH 0) or a vertical capsule (the station spindle). */
export interface EwSolid { x: number; y: number; z: number; r: number; halfH: number }
/** True when the segment a→b passes through no solid. A solid that contains an endpoint does not block (a hull at a
 *  station's skin is not "behind" it). */
export function losClear(a: { x: number; y: number; z: number }, b: { x: number; y: number; z: number }, solids: readonly EwSolid[]): boolean {
  const dx = b.x - a.x, dy = b.y - a.y, dz = b.z - a.z;
  const L2 = dx * dx + dy * dy + dz * dz;
  for (const s of solids) {
    const inside = (p: { x: number; y: number; z: number }): boolean => {
      const cy = s.halfH > 0 ? s.y + Math.max(-s.halfH, Math.min(s.halfH, p.y - s.y)) : s.y;
      return Math.hypot(p.x - s.x, p.y - cy, p.z - s.z) < s.r;
    };
    if (inside(a) || inside(b)) continue;
    // Closest approach of the segment to the solid's axis: sample the capsule axis as a sphere at the closest height.
    let best = Infinity;
    const steps = s.halfH > 0 ? 8 : 0;
    for (let k = 0; k <= steps; k++) {
      const cy = s.halfH > 0 ? s.y - s.halfH + (2 * s.halfH * k) / steps : s.y;
      const t = L2 > 0 ? Math.max(0, Math.min(1, ((s.x - a.x) * dx + (cy - a.y) * dy + (s.z - a.z) * dz) / L2)) : 0;
      const d = Math.hypot(a.x + dx * t - s.x, a.y + dy * t - cy, a.z + dz * t - s.z);
      if (d < best) best = d;
    }
    if (best < s.r) return false;
  }
  return true;
}

// ── Request ids (a duplicate never spends twice) ───────────────────────────────────────────────────────────────────
/** The ring is `id=fingerprint` entries joined by ','. A seen id with the same fingerprint is a DUPLICATE (no-op); a
 *  seen id with another fingerprint is a CONFLICT (refused: one id, one request); otherwise it is new. */
export function reqSeen(ring: string, reqId: string, fp: string): 'new' | 'dup' | 'conflict' {
  for (const e of ring ? ring.split(',') : []) {
    const i = e.indexOf('=');
    if (i < 0) continue;
    if (e.slice(0, i) === reqId) return e.slice(i + 1) === fp ? 'dup' : 'conflict';
  }
  return 'new';
}
export function reqRemember(ring: string, reqId: string, fp: string): string {
  const list = ring ? ring.split(',').filter((e) => e.length > 0) : [];
  list.push(`${reqId}=${fp}`);
  while (list.length > EW_REQ_RING) list.shift();
  return list.join(',');
}

// ── A playtest-style policy model (verify:space-flight runs it — the report's disconfirmation rule) ───────────────
export type DuelPolicy = 'none' | 'burst' | 'burst_late' | 'escape';
/** One jammer (re-applying at its first chance) against one pilot working a metered job of `needMs`, under a
 *  counterplay policy, in 100 ms steps. The pilot NOTICES a jam `reactMs` after it lands (the HUD strip, a key press,
 *  the round trip); `escape` boosts straight away at `escapeMps` while jammed (the work stops while beyond 600 m of the
 *  site, which sits beside the jammer) and returns when clear. Returns the job's completion, the time spent jammed and
 *  the counts — an obligatory immunity button (burst ⇒ no exposure on every job length) or a meaningless one (burst ⇒ no
 *  change) shows in the numbers. */
export function ewDuel(policy: DuelPolicy, needMs = 12_000, opts: { reactMs?: number; escapeMps?: number; horizonMs?: number } = {}): { doneMs: number; jammedMs: number; bursts: number; jams: number } {
  const reactMs = opts.reactMs ?? 1_000, escapeMps = opts.escapeMps ?? 260, horizonMs = opts.horizonMs ?? 300_000;
  let progress = 0, jamReady = 0, burstReady = 0, resistUntil = 0, effFrom = -1, effUntil = 0, jammedMs = 0, bursts = 0, jams = 0;
  let sep = 300, farSince = 0;
  const STEP = 100;
  for (let t = 0; t <= horizonMs; t += STEP) {
    // The jammer re-applies at its first chance (in range, not resisted, nothing standing).
    if (t >= jamReady && sep <= EW_RANGE_M && t >= resistUntil && t >= effUntil) { effFrom = t; effUntil = t + EW_DURATION_MS; jamReady = t + EW_COOLDOWN_MS; jams++; farSince = 0; }
    const noticed = t < effUntil && t - effFrom >= reactMs;
    if (noticed && t >= burstReady && (policy === 'burst' || (policy === 'burst_late' && effUntil - t <= 3_000))) {
      effUntil = t; resistUntil = t + ECCM_RESIST_MS; burstReady = t + ECCM_COOLDOWN_MS; bursts++;
    }
    if (policy === 'escape') sep = noticed ? Math.min(3_000, sep + escapeMps * STEP / 1000) : t < effUntil ? sep : Math.max(300, sep - escapeMps * STEP / 1000);
    if (t < effUntil) {
      const b = breakStep(farSince, sep, t);
      farSince = b.farSinceMs;
      if (b.brokeAtMs !== null) effUntil = Math.max(t, b.brokeAtMs);
    }
    const j = t < effUntil;
    if (j) jammedMs += STEP;
    if (sep <= EW_RANGE_M) progress += STEP * (j ? 1 - EW_STRENGTH : 1);
    if (progress >= needMs) return { doneMs: t + STEP, jammedMs, bursts, jams };
  }
  return { doneMs: Infinity, jammedMs, bursts, jams };
}
